import React, { FC, Fragment } from 'react';
import { Order } from 'models/order';
import { Box, Button, TableCell, TableRow, Typography } from '@mui/material';
import { formatCurrency, formatDate } from 'utils/format';
import RestorationTypes from 'components/orders/shared/restoration-types.component';
import { useTranslation } from 'react-i18next';
import DoneIcon from '@mui/icons-material/Done';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type CertifyOrderListItemProps = {
    order: Order;
    isDentist: boolean;
    openPriceSpecDialog: () => void;
    openConfirmDialog: () => void;
};
const CertifyOrderListItem: FC<CertifyOrderListItemProps> = ({
    order,
    isDentist,
    openPriceSpecDialog,
    openConfirmDialog,
}: CertifyOrderListItemProps) => {
    const { t: translate } = useTranslation(['orders']);

    const navigateToOrderDetails = () => {
        window.open(`/order-details/${order._id}/main-view`, '_blank');
    };

    return (
        <Fragment>
            <TableRow sx={{ cursor: 'pointer' }} hover onClick={navigateToOrderDetails}>
                <TableCell sx={{ maxWidth: 80 }}>
                    {isDentist ? (
                        order?.isCertified ? (
                            <Box
                                p={0.5}
                                sx={{ color: 'success.main', cursor: 'pointer' }}
                                display="flex"
                                justifyItems="center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openConfirmDialog();
                                }}
                            >
                                <CheckCircleIcon fontSize="small" />
                                <Box ml={0.5}>{translate('orders:CERTIFIED')}</Box>
                            </Box>
                        ) : (
                            <Button
                                startIcon={<DoneIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openPriceSpecDialog();
                                }}
                            >
                                {translate('orders:CERTIFY')}
                            </Button>
                        )
                    ) : undefined}
                </TableCell>
                <TableCell>
                    <div>{order.orderId}</div>
                </TableCell>
                <TableCell sx={{ maxWidth: 150 }}>
                    <Box>
                        {order.patient?.firstName}&nbsp;{order.patient?.lastName}
                    </Box>
                    {order.patient?.personalIdentityNumber ? (
                        <Typography variant="body2" color="grey.700">
                            {order.patient?.personalIdentityNumber}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="grey.700">
                            {translate('orders:NO_PIN_NUMBER')}
                        </Typography>
                    )}
                </TableCell>
                <TableCell sx={{ maxWidth: 190 }}>
                    <RestorationTypes sampleType={order.deliveryDetails?.sampleType} types={order.restorationTypes} />
                </TableCell>
                <TableCell sx={{ minWidth: 140, maxWidth: 180 }}>{order.supplier?.name}</TableCell>
                <TableCell sx={{ minWidth: 125, maxWidth: 140 }}>
                    <Box>{formatDate(new Date(order.created?.on), 'date')}</Box>
                    {order.created?.by?.fullName !== order.dentist?.fullName ? (
                        <Box>
                            <Typography variant="caption">{order.created?.by.fullName}</Typography>
                        </Box>
                    ) : undefined}
                </TableCell>
                <TableCell align="right">
                    <Box>{formatCurrency(order.totalAmount?.totalPrice || 0, order.currency)}</Box>
                    <Box sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">
                            {translate('orders:VAT')}:{' '}
                            {formatCurrency(order.totalAmount?.vatAmount || 0, order.currency)}
                        </Typography>
                    </Box>
                </TableCell>
            </TableRow>
        </Fragment>
    );
};

export default CertifyOrderListItem;
